import { useEffect } from "react";
import { PUBLIC_DOMAIN } from "@/lib/api";

function upsertMeta(attr, key, content) {
  if (content == null) return;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertLink(rel, href) {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

export function useSeo({
  title,
  description,
  path = "/",
  noindex = false,
  jsonLd = null,
  ogImageAlt = "Satu Soalan untuk PMX. Hantar satu soalan anda sebelum 1 September.",
  ogImage = "/og-satu-soalan.jpg",
}) {
  useEffect(() => {
    if (title) document.title = title;
    const canonical = `${PUBLIC_DOMAIN}${path === "/" ? "/" : path}`;

    upsertMeta("name", "description", description);
    upsertMeta(
      "name",
      "robots",
      noindex ? "noindex, nofollow" : "index, follow, max-image-preview:large",
    );
    upsertLink("canonical", canonical);

    upsertMeta("property", "og:type", "website");
    upsertMeta("property", "og:site_name", "Satu Soalan");
    upsertMeta("property", "og:locale", "ms_MY");
    upsertMeta("property", "og:title", title);
    upsertMeta("property", "og:description", description);
    upsertMeta("property", "og:url", canonical);
    upsertMeta("property", "og:image", `${PUBLIC_DOMAIN}${ogImage}`);
    upsertMeta("property", "og:image:secure_url", `${PUBLIC_DOMAIN}${ogImage}`);
    upsertMeta("property", "og:image:type", "image/jpeg");
    upsertMeta("property", "og:image:width", "1200");
    upsertMeta("property", "og:image:height", "630");
    upsertMeta("property", "og:image:alt", ogImageAlt);
    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:site", "@sembangohhyeahh");
    upsertMeta("name", "twitter:title", title);
    upsertMeta("name", "twitter:description", description);
    upsertMeta("name", "twitter:image", `${PUBLIC_DOMAIN}${ogImage}`);
    upsertMeta("name", "twitter:image:alt", ogImageAlt);

    const existing = document.getElementById("ss-jsonld");
    if (existing) existing.remove();
    if (jsonLd) {
      const script = document.createElement("script");
      script.type = "application/ld+json";
      script.id = "ss-jsonld";
      script.text = JSON.stringify(jsonLd);
      document.head.appendChild(script);
    }
  }, [title, description, path, noindex, jsonLd, ogImageAlt, ogImage]);
}

export const ORG_JSONLD = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "SembangOhhyeahh",
  url: PUBLIC_DOMAIN,
  description:
    "Podcast bebas yang menganjurkan kempen penyertaan nasional Satu Soalan untuk PMX.",
};

export const WEBSITE_JSONLD = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: "Satu Soalan untuk PMX",
  alternateName: "#SatuSoalanPMX",
  url: PUBLIC_DOMAIN,
  inLanguage: "ms-MY",
  description:
    "Kempen penyertaan rakyat untuk mengumpul satu soalan daripada rakyat kepada PMX.",
  publisher: {
    "@type": "Organization",
    name: "SembangOhhyeahh",
    url: PUBLIC_DOMAIN,
  },
};

export const campaignEventJsonLd = ({ startDate, deadline }) => ({
  "@context": "https://schema.org",
  "@type": "Event",
  name: "Satu Soalan untuk PMX",
  description:
    "Sesi podcast SembangOhhyeahh bersama PMX. Soalan dikumpul daripada rakyat melalui kempen Satu Soalan.",
  eventAttendanceMode: "https://schema.org/OnlineEventAttendanceMode",
  eventStatus: "https://schema.org/EventScheduled",
  inLanguage: "ms-MY",
  startDate: startDate || undefined,
  url: PUBLIC_DOMAIN,
  organizer: {
    "@type": "Organization",
    name: "SembangOhhyeahh",
    url: PUBLIC_DOMAIN,
  },
  location: {
    "@type": "VirtualLocation",
    url: PUBLIC_DOMAIN,
  },
  ...(deadline ? { doorTime: deadline } : {}),
});
